/* eslint-disable camelcase */
import React, { useState, useEffect, useCallback } from 'react';
import { Form } from '@fulcrumapp/fulcrum-core';
import {
  sortBy, capitalize, uniqBy, isEmpty, uniq, flattenDeep,
} from 'lodash';
import uuid from 'uuid';
import { Typography } from '@mui/material';
import { useProcess } from '../hooks';
import AddMemberSummary from './add-member-summary';
import AddMembers from './add-members';
import { Row, Role as MappedRole } from './add-member-row';
import ImportMembers, { ImportSchema, ImportData } from '../shared/import-members';
import {
  Permissions,
  SelectedPermissions,
  UpdatedPermission,
} from '../shared/permissions';
import Textarea from '../shared/textarea';
import Loader from '../shared/loader';
import Modal from '../components/modals/modal';
import css from '../shared/css';
import { Role } from '../models/role';
import { Error } from '../models/error';
import {
  getEmails,
  updateRecord,
  hasErroredRow,
  noUsersEntered,
  removeDuplicates,
  membersHavePermissions,
} from '../utils/methods';
import { useApp } from '../hooks/use-app';
import Group, { GroupsInheritedPermissions, transformGroup } from '../models/group';

interface Props {
  memberships: Member[];
  isManaged: boolean;
  isOpen: boolean;
  billableCount: number;
  includedUsers: number;
  minLicensePurchase?: number;
  availableLicenses: number;
  onClose: () => void;
  onSubmit: () => void;
  hasImports: (imported: boolean) => void;
}

export interface Member {
  _id?: string;
  id?: string;
  email: string;
  first_name: string;
  last_name: string;
  role_id: string;
  valid?: boolean;
  imported?: boolean;
  existing_user?: boolean;
  errors?: Error[] | string[];
}

export interface CurrentMember {
  isManaged: boolean;
  maxMembers: number;
  minLicensePurchase?: number;
  existingMembers: string[];
}

interface PartialPermissions {
  groups: any[];
  forms: Form[];
  layers: any[];
  projects: any[];
}

export function maxError(max: number): Error {
  return {
    type: 'Max User Error',
    message: `Your plan allows a maximum of ${max} users. Contact Support for more details`,
  };
}

export function renderErrors(erroredMembers: Member[]) {
  const memberErrors = erroredMembers.map((mem) => mem.errors);
  const errors = uniq(flattenDeep(memberErrors as string[][]));

  return (
    <ul style={{ marginBottom: '0', marginLeft: '15px' }}>
      {errors.map((err) => (
        <li key={`error-${err}`}>
          Error:
          {err}
        </li>
      ))}
    </ul>
  );
}

export const AddMemberModal: React.FC<Props> = ({
  isOpen,
  onClose,
  onSubmit,
  isManaged,
  memberships,
  billableCount,
  includedUsers,
  minLicensePurchase,
  availableLicenses,
  hasImports,
}: Props) => {
  const newRow = {
    _id: uuid.v4(),
    first_name: '',
    last_name: '',
    email: '',
    role_id: '',
    valid: false,
    existing_user: false,
    imported: false,
    errors: [],
  };

  const app = useApp();
  const { status, dispatch } = useProcess();
  const [importData, setImportData] = useState<ImportSchema>();
  const [importErrorsFixed, setImportErrorsFixed] = useState(false);
  const [formError, setFormError] = useState(true);
  const [showSummary, setSummary] = useState(false);
  const [maxMembersReached, setMaxMembersReached] = useState(false);
  const [roles, setRoles] = useState<MappedRole[]>([]);
  const [rolesDefault, setRolesDefault] = useState<MappedRole>();
  const [comment, setComment] = useState('');
  const [rows, setRows] = useState<Row[]>([newRow]);
  const [permissions, setPermissions] = useState<PartialPermissions>({
    groups: [],
    forms: [],
    layers: [],
    projects: [],
  });
  const [selectedPermissions, setSelectedPermissions] = useState<SelectedPermissions>({
    selectedGroups: [],
    selectedForms: [],
    selectedLayers: [],
    selectedProjects: [],
  });
  const [groupsInheritedPermissions,
    setGroupsInheritedPermissions] = useState<GroupsInheritedPermissions>(
    Group().getSelectedGroupsPermissions(),
  );

  const currentMember: CurrentMember = {
    isManaged,
    maxMembers: app.globals.organization.max_users,
    minLicensePurchase,
    existingMembers: getEmails(memberships),
  };

  const { maxMembers, existingMembers, minLicensePurchase: minLicensePurchaseValue } = currentMember;
  const invalidRow = 'Make sure your rows are filled and valid';
  const message = maxMembersReached ? maxError(maxMembers).message : invalidRow;

  useEffect(() => {
    async function fetchData() {
      const [groups, projects, layers, forms, allRoles] = await Promise.all([
        fetchAllGroups(),
        fetchAllProjects(),
        fetchAllLayers(),
        fetchAllForms(),
        getRoles(),
      ]);
      const defaultRole = allRoles.find((role: MappedRole) => role.default) ?? allRoles[0];

      const id = defaultRole.value;

      setRolesDefault(defaultRole);
      setRoles([...allRoles]);
      setRows([{ ...newRow, role_id: id }]);
      setPermissions({
        forms, layers, projects, groups,
      });

      dispatch({ type: 'LOADED' });
    }

    fetchData()
      .catch((e) => {
        dispatch({ type: 'ERROR', error: { type: 'Fetch Error', message: 'There was an error fetching data required to add members' } });
        console.error(e);
      });
  }, []);

  useEffect(() => {
    const inheritedPermissions = Group().getSelectedGroupsPermissions(
      { allGroups: permissions.groups, selectedGroups: selectedPermissions.selectedGroups },
    );
    setGroupsInheritedPermissions(inheritedPermissions);
    const { inheritedFormIds, inheritedLayerIds, inheritedProjectIds } = inheritedPermissions;
    // Make sure that if something is inherited it is removed from the selected permissions
    setSelectedPermissions((prevState) => ({
      ...prevState,
      selectedForms: prevState.selectedForms.filter(
        (form) => !inheritedFormIds.includes(form),
      ),
      selectedLayers: prevState.selectedLayers.filter(
        (layer) => !inheritedLayerIds.includes(layer),
      ),
      selectedProjects: prevState.selectedProjects.filter(
        (project) => !inheritedProjectIds.includes(project),
      ),
    }));
  }, [selectedPermissions.selectedGroups]);

  // As a result of the rows changing, the max number is checked for validity
  useEffect(() => {
    setFormError(hasErroredRow(rows));

    if (!hasErroredRow(rows) && !importErrorsFixed && status === 'complete') {
      setImportErrorsFixed(true);
    }

    if (maxMembers && ((billableCount + rows.length - includedUsers) >= maxMembers)) {
      setMaxMembersReached(true);
    } else {
      setMaxMembersReached(false);
    }
  }, [rows]);

  async function getRoles() {
    const { objects } = await (app.api.roles as any).all(); // these would involve proper response typings

    const mapRoles = objects.map((role: Role) => ({ label: role.name, value: role.id, default: role.is_default }));

    const sortedRoles = sortBy(mapRoles, [(role) => role.label.toLowerCase()], 'label');
    return sortedRoles;
  }

  async function fetchAllForms() {
    try {
      const forms: Form[] = (await app.api.query.run({
        q: 'SELECT form_id AS id, name FROM forms WHERE system_type IS NULL ORDER BY name',
      })).objects;

      return forms;
    } catch (error) {
      console.error(error);
      return [];
    }
  }

  async function fetchAllProjects() {
    return (
      await app.api.query.run({
        q: 'SELECT project_id AS id, name FROM projects ORDER BY name',
      })
    ).objects;
  }

  async function fetchAllGroups() {
    const { objects } = await (app.api.groups as any).all({ associations: true });

    const transformedObjects = objects.map((item: any) => transformGroup(item));

    return sortBy(transformedObjects, 'name');
  }

  async function fetchAllLayers() {
    return (await (app.api.layers as any).all()).objects;
  }

  const handleUpdateRow = useCallback((updatedRow: Row) => {
    const rowsCopy = [...rows];
    const newRows: Row[] = updateRecord(rowsCopy, updatedRow);
    setRows(newRows);
  }, [rows]);

  const handleAddNewRow = useCallback(() => {
    setMaxMembersReached(false);
    setRows([...rows, { ...newRow, role_id: rolesDefault?.value ?? '' }]);
  }, [rows, rolesDefault]);

  const handleRemoveRow = useCallback((id: string) => {
    setMaxMembersReached(false);
    const filteredRows = rows.filter((row) => row._id !== id);
    setRows([...filteredRows]);
  }, [rows]);

  function handleUpdatePermissions(updated: UpdatedPermission) {
    const permissionType = `selected${capitalize(updated.type)}`;
    setSelectedPermissions((previousPermissions) => ({
      ...previousPermissions,
      [permissionType]: updated.changes,
    }));
  }

  const handleReviewChanges = useCallback(() => {
    const conditions = (item: Member) => [
      item.email,
      item.first_name,
      item.last_name,
      item.role_id,
    ];
    const confirmed = membersHavePermissions({
      result: true,
      permissions,
      selectedPermissions,
    });

    let uniqAccounts: Row[] = [];
    const getAccounts = rows.filter((mem) => (
      mem.existing_user && !existingMembers.includes(mem.email)));

    if (getAccounts.length) {
      uniqAccounts = uniqBy(getAccounts, (acc) => JSON.stringify(conditions(acc)));
    }

    const uniqRows = uniqBy([...rows, ...uniqAccounts], (acc) => JSON.stringify(conditions(acc)));
    setRows(uniqRows);

    if (confirmed) {
      setSummary(true);
    }
  }, [permissions, selectedPermissions, rows, existingMembers]);

  function handleRemoveExisting(existing: string[]) {
    const validRows = removeDuplicates(existing, rows);

    if (!validRows.length) {
      setRows([{ ...newRow, role_id: rolesDefault?.value ?? '' }]);
    } else {
      setRows([...validRows]);
    }
  }

  const displayTooltip = useCallback(() => {
    if (maxMembersReached || formError) {
      return message;
    }
    return '';
  }, [maxMembersReached, formError, message]);

  function handleSave() {
    setSummary(false);
    onSubmit();
  }

  const handleUploadFile = useCallback(async (data: ImportData[]) => {
    dispatch({ type: 'STARTED' });

    let rowsCopy = [...rows];
    const emptyRows = noUsersEntered([...rowsCopy]);
    const invalidRows = rowsCopy.filter((row) => !row.valid && row._id && !emptyRows.includes(row._id));

    const members = {
      roles,
      managed: isManaged,
      total: data.length + existingMembers.length + (rowsCopy.length - (emptyRows.length + invalidRows.length)),
      max: maxMembers,
    };

    const processedData = await ImportMembers({ data, members });

    if ((processedData as ImportSchema).all) {
      const { success, errors } = processedData as ImportSchema;
      const importMembers = [...success];

      if (!isEmpty(errors)) {
        importMembers.unshift(...errors);
      }

      if (emptyRows.length) {
        rowsCopy = rowsCopy.filter((row) => row._id && !emptyRows.includes(row._id));
      }

      if (invalidRows.length) {
        rowsCopy = rowsCopy.filter((row) => row.valid);
      }

      setRows([...rowsCopy, ...(importMembers as Row[])]);

      dispatch({ type: 'SUCCESS' });
      setImportData(processedData as ImportSchema);
      hasImports(true);
    } else {
      dispatch({ type: 'ERROR', error: processedData as Error });
    }
  }, [dispatch, rows, roles, isManaged, existingMembers, maxMembers, hasImports]);

  function renderSummary() {
    return (
      <AddMemberSummary
        show={showSummary}
        width={700}
        member={currentMember}
        members={rows}
        comment={comment}
        positive="Confirm changes"
        confirmed={() => handleSave()}
        cancelled={() => setSummary(false)}
        permissions={selectedPermissions}
        orgMembers={memberships}
        removeExisting={(existing) => handleRemoveExisting(existing)}
        availableLicenses={availableLicenses}
        minimumLicensePurchase={minLicensePurchaseValue}
        billableCount={billableCount}
        includedUsers={includedUsers}
      />
    );
  }

  const renderChanges = useCallback((changes: string[]) => (
    <ul style={{ marginBottom: '0', marginLeft: '15px' }}>
      {changes.map((change, i) => (
        // since changes are strings, index is acceptable here
        // eslint-disable-next-line react/no-array-index-key
        <li key={i}>{change}</li>
      ))}
    </ul>
  ), []);

  return (
    <Modal
      hideTitle
      slideTop
      open={isOpen}
      onClose={onClose}
      positive="Next"
      positiveDisabled={formError}
      onNegative={onClose}
      onPositive={handleReviewChanges}
    >
      {status === 'initial load' && <Loader />}
      <div className={styles.container} style={{ opacity: status === 'initial load' ? 0 : 1 }}>
        <AddMembers
          rows={rows}
          roles={roles}
          rolesDefault={rolesDefault}
          isManaged={isManaged}
          status={status}
          maxMembersReached={maxMembersReached}
          formError={formError}
          importData={importData}
          importErrorsFixed={importErrorsFixed}
          onUpdateRow={handleUpdateRow}
          onAddNewRow={handleAddNewRow}
          onRemoveRow={handleRemoveRow}
          onUploadFile={handleUploadFile}
          displayTooltip={displayTooltip}
          renderErrors={renderErrors}
          renderChanges={renderChanges}
          availableLicenses={availableLicenses}
          minimumLicensePurchase={minLicensePurchaseValue}
        />
        <hr />
        <Permissions
          forms={permissions.forms}
          layers={permissions.layers}
          projects={permissions.projects}
          groups={permissions.groups}
          selectedForms={selectedPermissions.selectedForms}
          selectedLayers={selectedPermissions.selectedLayers}
          selectedProjects={selectedPermissions.selectedProjects}
          selectedGroups={selectedPermissions.selectedGroups}
          groupsInheritedPermissions={groupsInheritedPermissions}
          updatePermissions={(update) => handleUpdatePermissions(update)}
        />
        <hr />
        <Typography variant="h6" sx={{ mb: 2 }}>
          4. Add a note to the invitation email (optional)
        </Typography>
        <Textarea text={comment} updateText={(e) => setComment(e.target.value)} />
        {showSummary && renderSummary()}
      </div>
    </Modal>
  );
};

const styles = css<React.CSSProperties>({
  container: {
    transition: 'opacity .25s',
  },
  addOption: {
    fontSize: 13,
    padding: '0 4px 0 0',
  },
  title: {
    marginBottom: 10,
  },
  error: {
    fontSize: '.8em',
    color: '#f00',
  },
});
